import { workspaceBlockMeta, type WorkspaceBlockPreviewModel } from "@/lib/workspace-block-registry";

/** Versionless compact silhouettes: only block appearance, counts and nested
 * columns. No text, ids, URLs or business data from the document are copied.
 * Legacy shapes remain readable during rollout of saved page/template previews. */
export type PageBlockMiniature = {
  kind: "miniature";
  model: WorkspaceBlockPreviewModel;
  count?: number;
  children?: PageTemplateThumb[];
};
export type LegacyPageTemplateThumbBlock =
  | { kind: "heading"; text: string }
  | { kind: "text"; text: string }
  | { kind: "metrics"; items: Array<{ label: string; unit: string }> }
  | { kind: "chart"; title?: string; variant?: "line" | "bar" | "hbar" | "funnel" }
  | { kind: "table"; title?: string; columns: string[] }
  | { kind: "product"; title?: string }
  | { kind: "callout"; text: string }
  | { kind: "checklist"; items: string[] };

export type PageTemplateThumbBlock = PageBlockMiniature | LegacyPageTemplateThumbBlock;
export type PageTemplateThumb = PageTemplateThumbBlock[];
export const PAGE_THUMB_MAX_BLOCKS = 6;

type Node = Record<string, unknown>;
const isNode = (value: unknown): value is Node => !!value && typeof value === "object" && !Array.isArray(value);

/** Preserve the first visible blocks in document order. The miniature is an
 * opening fragment, not a reordered selection of the most prominent widgets.
 * Only structural attrs are read. Column recursion and node visits are bounded. */
export function buildPageTemplateThumb(document: unknown): PageTemplateThumb {
  let remaining = 60;
  const build = (content: unknown, depth: number): PageTemplateThumb => {
    if (!Array.isArray(content) || depth > 2) return [];
    const result: PageTemplateThumb = [];
    for (const node of content) {
      if (result.length >= PAGE_THUMB_MAX_BLOCKS || remaining-- <= 0) break;
      if (!isNode(node) || typeof node.type !== "string" || node.type === "text" || node.type === "hardBreak") continue;
      // Empty editor trailing paragraphs have no visible content.
      if (node.type === "paragraph" && (!Array.isArray(node.content) || !node.content.length)) continue;
      const attrs = isNode(node.attrs) ? node.attrs : {};
      const miniature: PageBlockMiniature = {kind: "miniature", model: workspaceBlockMeta(node.type).preview};
      if (node.type === "chartBlock") {
        miniature.model = attrs.chartType === "line" ? "chart-line" : attrs.chartType === "hbar" ? "chart-hbar" : "chart-bar";
      }
      if (node.type === "metrics" || node.type === "storeMetrics") miniature.count = Array.isArray(attrs.metrics) ? Math.min(4, Math.max(1, attrs.metrics.length)) : 3;
      if (node.type === "columnList" && Array.isArray(node.content) && depth < 2) {
        miniature.children = node.content.slice(0, 3).filter(isNode).map(column => build(column.content, depth + 1));
      }
      result.push(miniature);
    }
    return result;
  };
  return build(isNode(document) ? document.content : null, 0);
}

/** Convert old saved metadata to the same anonymous silhouettes on read. */
export function pageBlockMiniature(block: PageTemplateThumbBlock): PageBlockMiniature {
  if (block.kind === "miniature") return block;
  switch (block.kind) {
    case "chart": return {kind:"miniature", model: block.variant === "line" ? "chart-line" : block.variant === "hbar" ? "chart-hbar" : block.variant === "funnel" ? "funnel" : "chart-bar"};
    case "metrics": return {kind:"miniature",model:"metrics",count:Math.max(1,Math.min(4,block.items.length))};
    default: return {kind:"miniature",model:block.kind};
  }
}
